import { NavLink } from "react-router";
import type { ReactNode } from "react";
import { Card, SectionHeader } from "../design-system/index.js";

/**
 * <TaxLayout> — shared header + sub-nav for all /tax/* pages.
 *
 * Composes from design-system primitives only. Token-driven; both
 * themes via `var(--color-*)`. Underline-tab style mirrors the Tabs
 * design-system primitive (Phase 6.2). Renders a CPA disclaimer banner
 * above every sub-page so the household never confuses Hearth's
 * projection with filing math.
 *
 * Phase 7 Session 1.
 */

export interface TaxLayoutProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
  readonly action?: ReactNode;
}

const SUB_NAV: { readonly label: string; readonly to: string }[] = [
  { label: "Overview", to: "/tax" },
  { label: "Quarterly", to: "/tax/quarterly" },
  { label: "Capital gains", to: "/tax/capital-gains" },
  { label: "Roth ladder", to: "/tax/roth-ladder" },
  { label: "72(t) / SEPP", to: "/tax/sepp" },
  { label: "ACA planner", to: "/tax/aca-planner" },
  { label: "Documents", to: "/tax/documents" },
];

export function TaxLayout({ title, subtitle, action, children }: TaxLayoutProps) {
  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6 lg:p-8">
      <SectionHeader title={title} sub={subtitle} action={action} />
      <nav
        aria-label="Tax sections"
        className="flex flex-wrap gap-1 border-b border-[var(--color-border-subtle)]"
      >
        {SUB_NAV.map((entry) => (
          <NavLink
            key={entry.to}
            to={entry.to}
            end={entry.to === "/tax"}
            className={({ isActive }) =>
              isActive
                ? "-mb-px border-b-2 border-[var(--color-accent)] px-3 py-2 text-sm font-medium text-[var(--color-text)]"
                : "-mb-px border-b-2 border-transparent px-3 py-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-text)]"
            }
          >
            {entry.label}
          </NavLink>
        ))}
      </nav>
      <Card className="border border-[var(--color-warning)] bg-[var(--color-warning-soft)] p-3 text-xs text-[var(--color-text)]">
        Tax math here is informational, not tax advice. The numbers help with FIRE planning + safe-harbor signaling. Consult a CPA before filing or paying estimated taxes.
      </Card>
      {children}
    </div>
  );
}
